// refund.view — dialog Pengembalian Dana (CS ajukan) + Nota Pengembalian (print/WA).
import { rp, rpFmt, rpVal, esc, wa62, tglHuman, fillTpl, pecahUnit } from '@domain/format';
import { openModal } from '@ui/modal';
import { toast } from '@ui/toast';
import { previewDoc } from '@modules/documents/documents.view';
import { openWA } from '@core/wa';
import { waTpl, profil } from '@core/settings';
import { refreshCounts } from '@core/nav';
import * as svc from './refund.service';

export async function openRefundDialog(no: string, done: () => void, onBack?: () => void): Promise<void> {
  let pf: Awaited<ReturnType<typeof svc.prefill>>;
  try { pf = await svc.prefill(no); } catch (e) { toast((e as Error).message); return; }
  const p = profil();
  // Default jumlah = usul teknisi (dibatasi kas dibayar) bila ada, else kas dibayar. Port v0.18 refundDialog.
  const usulDefault = (pf.usul && pf.usul.amount != null) ? Math.min(pf.usul.amount, pf.paid) : pf.paid;

  const bg = openModal(`
    <div class="modal-head" style="background:var(--dangerbg)"><h3 style="color:var(--danger)">💸 Pengembalian Dana Konsumen</h3><button class="x">×</button></div>
    <div class="modal-body">
      <div class="panel" style="padding:10px;margin-bottom:10px;background:var(--dangerbg);border-radius:8px;font-size:13px">
        <div><b>${esc(no)}</b> · ${esc(pf.custNama)} · ${esc(pf.unit)}</div>
        <div style="margin-top:4px">Total sudah dibayar pelanggan (termasuk sesi sebelumnya): <b>${rp(pf.paid)}</b></div>
        ${pf.debt > 0 ? `<div style="margin-top:2px;color:var(--warn)">Sisa hutang (tempo): <b>${rp(pf.debt)}</b> → <b>DIBEBASKAN (lunas)</b> setelah Finance verifikasi.</div>` : ''}
      </div>
      ${pf.usul ? `<div class="panel" style="padding:8px 10px;margin-bottom:10px;background:var(--warnbg);border:1px solid var(--warn);border-radius:8px;font-size:12px">🔧 <b>Usul teknisi</b>: ${rp(pf.usul.amount)}${pf.usul.by ? ` (${esc(pf.usul.by)})` : ''}${pf.usul.note ? ` — ${esc(pf.usul.note)}` : ''}. Nominal boleh disesuaikan CS.</div>` : ''}
      <div class="field"><label>Nama Penerima Dana <span class="req">*</span></label><input id="rf_nama" value="${esc(pf.custNama)}"></div>
      <div class="field"><label>Jumlah Dikembalikan (kas) <span style="color:var(--muted);font-weight:400">— boleh Rp 0 bila hanya membebaskan hutang</span></label><input id="rf_amount" value="${rpFmt(String(usulDefault))}" inputmode="numeric"></div>
      <div class="field"><label>Metode Pengembalian</label><select id="rf_method"><option>Tunai</option><option>Transfer</option><option>QRIS</option><option>Potong Piutang</option><option>Voucher/Credit</option><option>Lainnya</option></select></div>
      <div id="rf_info" class="scope-note" style="font-size:12px"></div>
      <div class="field"><label>Catatan / Kesepakatan</label><textarea id="rf_reason" placeholder="mis. pengembalian penuh atas kegagalan servis (masih dalam kesepakatan)"></textarea></div>
      <div class="scope-note" style="color:var(--danger)">CS memproses; <b>Finance memverifikasi</b> di Status Pembayaran. Tiket pindah ke Sudah Diambil (status Refund).</div>
      <div class="err" id="rf_err"></div>
    </div>
    <div class="modal-foot"><button class="btn" id="rf_cancel">Batal</button><button class="btn" style="background:var(--danger);color:#fff;border-color:var(--danger)" id="rf_save">💸 Proses Pengembalian</button></div>`, { onBack });

  const amt = bg.querySelector('#rf_amount') as HTMLInputElement;
  amt.addEventListener('input', () => { amt.value = rpFmt(amt.value); });
  const method = bg.querySelector('#rf_method') as HTMLSelectElement;
  const info = bg.querySelector('#rf_info') as HTMLElement;
  const syncInfo = (): void => {
    info.innerHTML = method.value === 'Transfer' ? `Transfer ke rekening pelanggan (catat di catatan). Rekening toko: <b>${esc(p.rekening)}</b>`
      : method.value === 'QRIS' ? 'Pengembalian via QRIS/e-wallet pelanggan.' : 'Serahkan tunai ke pelanggan langsung.';
  };
  method.addEventListener('change', syncInfo); syncInfo();
  bg.querySelector('#rf_cancel')?.addEventListener('click', () => bg.remove());
  const saveBtn = bg.querySelector('#rf_save') as HTMLButtonElement;
  saveBtn.addEventListener('click', async () => {
    saveBtn.disabled = true;
    const nama = (bg.querySelector('#rf_nama') as HTMLInputElement).value;
    const meth = method.value;
    const reason = (bg.querySelector('#rf_reason') as HTMLTextAreaElement).value;
    const amount = rpVal(amt.value);
    try {
      await svc.submitRefund(no, amount, meth, reason, nama);
      bg.remove();
      notaRefundDialog({ no, custNama: pf.custNama, custWa: pf.custWa, unit: pf.unit, amount, method: meth, nama }, done);
    } catch (e) { const el = bg.querySelector('#rf_err'); if (el) el.textContent = (e as Error).message; saveBtn.disabled = false; }
  });
}

interface NotaRefundData { no: string; custNama: string; custWa: string; unit: string; amount: number; method: string; nama: string }

/** Dialog pasca-refund: cetak Nota Pengembalian / WA / selesai. Port notaRefundHTML v0.18. */
function notaRefundDialog(d: NotaRefundData, done: () => void): void {
  const bg = openModal(`<div class="modal-head" style="background:var(--dangerbg)"><h3 style="color:var(--danger)">💸 Pengembalian diproses — ${esc(d.no)}</h3><button class="x">×</button></div>
    <div class="modal-body"><div class="scope-note">Menunggu verifikasi Finance. Cetak / kirim Nota Pengembalian ke pelanggan (opsional):</div></div>
    <div class="modal-foot" style="flex-wrap:wrap">
      <button class="btn primary" id="nr_print">🖨️ Cetak Nota Pengembalian</button>
      <button class="btn" id="nr_wa">💬 Kirim WA</button>
      <button class="btn" id="nr_done">Selesai</button></div>`);
  const finish = (): void => { bg.remove(); toast('💸 Pengembalian diproses — menunggu verifikasi Finance'); done(); refreshCounts(); };
  bg.querySelector('#nr_print')?.addEventListener('click', () => { printNotaRefund(d); finish(); });
  bg.querySelector('#nr_wa')?.addEventListener('click', () => {
    const w = wa62(d.custWa);
    if (w) openWA(w, fillTpl(waTpl().refund, { nama: d.custNama, no: d.no, unit: d.unit, ...pecahUnit(d.unit), jumlah: rp(d.amount), via: d.method }));
    else toast('No. WA pelanggan tidak tersedia.');
    finish();
  });
  bg.querySelector('#nr_done')?.addEventListener('click', finish);
}

/**
 * Nota Pengembalian Dana — lewat `previewDoc` seperti dokumen lain, BUKAN `window.open` sendiri.
 * Dulu ia mencetak dengan ukuran bawaan browser (A4 tegak) sehingga setelan kertas
 * (Pengaturan → 🖨️ Cetak, bawaan ½ A4 melebar) tak berlaku untuknya — padahal dokumen ini
 * terdaftar di daftar dokumen dan wajar dikira ikut setelan yang sama.
 */
function printNotaRefund(d: NotaRefundData): void {
  const p = profil();
  const row = (k: string, v: string): string => `<tr><td style="color:#6b7a8a;padding:3px 8px 3px 0">${k}</td><td style="font-weight:600">${v}</td></tr>`;
  const html = `<!doctype html><html lang="id"><head><meta charset="utf-8"><title>Nota Pengembalian ${esc(d.no)}</title>
    <style>body{font-family:system-ui,-apple-system,sans-serif;padding:18px;color:#1d2733;font-size:13px}h2{margin:0;color:#0A2A4A}.t{color:#6b7a8a;font-size:11px}hr{border:none;border-top:1px dashed #dbe4ec;margin:10px 0}.big{font-size:20px;color:#c0392b;font-weight:800}.sign{margin-top:36px;display:flex;justify-content:space-between}.sign div{text-align:center;font-size:11px;color:#6b7a8a}</style></head>
    <body>
      <h2>${esc(p.namaUsaha)}</h2><div class="t">${esc(p.tagline)}${p.telp ? ' · ' + esc(p.telp) : ''}</div>
      <hr><div style="font-weight:700;letter-spacing:.5px;color:#c0392b">NOTA PENGEMBALIAN DANA</div>
      <table style="margin-top:6px">
        ${row('No. Servis', esc(d.no))}
        ${row('Pelanggan', esc(d.custNama))}
        ${row('Unit', esc(d.unit))}
        ${row('Metode', esc(d.method))}
        ${row('Tanggal', esc(tglHuman(new Date())))}
      </table>
      <hr><div>Jumlah dikembalikan:</div><div class="big">${rp(d.amount)}</div>
      <div class="t" style="margin-top:6px">Diterima oleh: ${esc(d.nama)}</div>
      <div class="sign"><div>_______________<br>Penerima Dana</div><div>_______________<br>Petugas iFiCs</div></div>
    </body></html>`;
  previewDoc(`Nota Pengembalian — ${d.no}`, '💸', html);
}
